import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { LivingOrderDialog } from "@/components/LivingOrderDialog";
import { Flame, Heart, ShieldCheck, Gift, Sparkles, Lock, Mail, ChevronLeft, ChevronUp, ChevronDown } from "lucide-react";

const euros = (c) => `${((c || 0) / 100).toFixed(((c || 0) % 100) ? 2 : 0)} €`;

export default function LivingIntro() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);

  const cerPrice = cfg?.living_prices?.ceremony_effective ?? cfg?.living_ceremony_price_cents ?? 2900;
  const msgPrice = cfg?.living_prices?.message_effective ?? cfg?.living_message_price_cents ?? 500;

  const openOrder = () => {
    if (!user) { setAuthOpen(true); return; }
    setOrderOpen(true);
  };

  return (
    <div className="living-sky relative min-h-screen pb-24">
      <div className="living-sky-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/living-sky.jpg)` }} aria-hidden="true" />
      <div className="sky-clouds" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/sky-clouds.png)` }} aria-hidden="true" />
      <div className="sky-clouds layer2" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/sky-clouds.png)` }} aria-hidden="true" />
      <Header />

      <div className="relative z-10 mx-auto max-w-2xl px-5 pt-6" data-testid="living-intro-page">
        <div className="mb-4">
          <button onClick={() => navigate("/profil")} data-testid="living-intro-back-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-slate-900/70 px-4 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xl transition-colors hover:bg-slate-800/80">
            <ChevronLeft size={16} /> {t("po.back_profile")}
          </button>
        </div>
        <div className="flex flex-col items-center text-center">
          <img src={`${process.env.PUBLIC_URL}/logo-postinity.png`} alt="Postinity" className="h-28 w-auto opacity-95 sm:h-32" style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,1)) drop-shadow(0 2px 5px rgba(0,0,0,0.95))" }} />
          <div className="mt-4 rounded-3xl border border-white/15 bg-slate-900/70 px-6 py-4 shadow-xl backdrop-blur-xl">
            <h1 className="text-3xl font-semibold text-white sm:text-4xl">
              {t("po.title")}
            </h1>
            <p className="mt-2 max-w-xl text-base text-white/90 sm:text-lg">
              {t("po.subtitle")}
            </p>
          </div>
        </div>

        {/* Le concept */}
        <div className="mt-7 rounded-3xl border border-white/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl" data-testid="living-intro-concept">
          <div className="mb-3 flex items-center gap-2 text-amber-300"><Sparkles size={18} /><h2 className="text-lg font-semibold">{t("po.concept_h")}</h2></div>
          <div className="space-y-4 text-base leading-relaxed text-white/90 sm:text-lg">
            <div>
              <p className="font-semibold text-white">{t("po.concept1_t")}</p>
              <p className="mt-1 text-white/85">{t("po.concept1_d")}</p>
            </div>
            <div>
              <p className="font-semibold text-white">{t("po.concept2_t")}</p>
              <p className="mt-1 text-white/85">{t("po.concept2_d")}</p>
            </div>
            <div>
              <p className="font-semibold text-white">{t("po.concept3_t")}</p>
              <p className="mt-1 text-white/85">{t("po.concept3_d")}</p>
            </div>
          </div>
        </div>

        {/* Pourquoi c'est gratuit */}
        <div className="mt-4 rounded-3xl border border-emerald-400/30 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl" data-testid="living-intro-free">
          <div className="mb-2 flex items-center gap-2 text-emerald-300"><Gift size={18} /><h2 className="text-lg font-semibold">{t("po.free_h")}</h2></div>
          <div className="space-y-3 text-base leading-relaxed text-white/90 sm:text-lg">
            <p>{t("po.free_p1")}</p>
            <p>{t("po.free_p2")}</p>
            <p>{t("po.free_p3")}</p>
            <p className="text-center text-lg font-semibold text-emerald-200 sm:text-xl">{t("po.free_tag1")}<br />{t("po.free_tag2")}</p>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => setShowPricing((v) => !v)} data-testid="living-intro-pricing-link"
              className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-300/90 underline underline-offset-2 transition-colors hover:text-sky-200">
              <ShieldCheck size={10} /> {t("po.pricing_link")} {showPricing ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          </div>
        </div>

        {/* Les tarifs (repliés, ouverts via le lien) */}
        {showPricing && (
        <div className="mt-4 rounded-3xl border border-white/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl" data-testid="living-intro-pricing">
          {/* Bloc GRATUIT — pour la personne qui dépose ses messages */}
          <div className="mb-3 flex items-start gap-2 text-emerald-300">
            <Gift size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-base font-semibold text-white sm:text-lg">{t("po.free_block_title")}</p>
              <h2 className="text-sm font-medium text-emerald-300">{t("po.free_block_sub")}</h2>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-emerald-400/25 bg-black/25 p-4 text-center" data-testid="living-intro-free-ceremony">
              <div className="mb-1 flex items-center justify-center gap-2 text-emerald-200"><Lock size={16} /><span className="text-sm font-semibold">{t("po.card_ceremony")}</span></div>
              <div className="text-2xl font-bold text-emerald-300">0 €</div>
              <p className="mt-1 text-xs text-white/75">{t("po.free_ceremony_desc")}</p>
            </div>
            <div className="rounded-2xl border border-emerald-400/25 bg-black/25 p-4 text-center" data-testid="living-intro-free-message">
              <div className="mb-1 flex items-center justify-center gap-2 text-emerald-200"><Mail size={16} /><span className="text-sm font-semibold">{t("po.card_message")}</span></div>
              <div className="text-2xl font-bold text-emerald-300">0 €</div>
              <p className="mt-1 text-xs text-white/75">{t("po.free_message_desc")}</p>
            </div>
          </div>

          <div className="mt-4 flex justify-center">
            <Button onClick={openOrder} data-testid="living-intro-pay-btn"
              className="h-auto whitespace-normal rounded-full border-0 px-4 py-2 text-xs font-semibold text-white transition-all hover:brightness-110 sm:text-sm"
              style={{ background: "linear-gradient(180deg, rgba(14,99,201,0.9) 0%, rgba(47,143,214,0.85) 60%, rgba(76,159,224,0.8) 100%)", textShadow: "0 1px 4px rgba(0,0,0,0.5)", boxShadow: "0 6px 14px rgba(0,0,0,0.45)" }}>
              <ShieldCheck size={14} className="mr-1.5 shrink-0" /> {t("po.pay_btn")}
            </Button>
          </div>

          <div className="my-5 border-t border-white/10" />

          {/* Bloc PAYANT — pour la personne qui consulte */}
          <div className="mb-3 flex items-start gap-2 text-sky-300">
            <ShieldCheck size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-base font-semibold text-white sm:text-lg">{t("po.paid_block_title")}</p>
              <h2 className="text-sm font-medium text-sky-300">{t("po.paid_block_sub")}</h2>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-amber-400/25 bg-black/25 p-4 text-center" data-testid="living-intro-price-ceremony">
              <div className="mb-1 flex items-center justify-center gap-2 text-amber-200"><Lock size={16} /><span className="text-sm font-semibold">{t("po.card_ceremony")}</span></div>
              <div className="text-2xl font-bold text-white">{euros(cerPrice)}</div>
              <p className="mt-1 text-xs text-white/75">{t("po.ceremony_paid_desc")}</p>
            </div>
            <div className="rounded-2xl border border-sky-400/25 bg-black/25 p-4 text-center" data-testid="living-intro-price-message">
              <div className="mb-1 flex items-center justify-center gap-2 text-sky-200"><Mail size={16} /><span className="text-sm font-semibold">{t("po.card_message")}</span></div>
              <div className="text-2xl font-bold text-white">{euros(msgPrice)}</div>
              <p className="mt-1 text-xs text-white/75">{t("po.message_paid_desc")}</p>
            </div>
          </div>
          <p className="mt-4 flex items-center justify-center gap-2 text-center text-base font-medium text-emerald-300 sm:text-lg"><Heart size={16} /> {t("po.free_footer")}</p>
        </div>
        )}

        {/* Bouton centré en bas */}
        <div className="mt-8 flex justify-center">
          <Button onClick={() => navigate("/postinity/preparer")} data-testid="living-intro-start-btn"
            className="h-16 rounded-full border-0 px-10 text-xl font-semibold text-white transition-all hover:brightness-110 sm:text-2xl"
            style={{ background: "linear-gradient(180deg, rgba(14,99,201,0.9) 0%, rgba(47,143,214,0.85) 60%, rgba(76,159,224,0.8) 100%)", textShadow: "0 1px 4px rgba(0,0,0,0.5)", boxShadow: "0 14px 28px rgba(0,0,0,0.55), 0 6px 12px rgba(0,0,0,0.45)" }}>
            <Flame size={26} className="mr-2.5 drop-shadow" /> {t("po.start_btn")}
          </Button>
        </div>
      </div>

      <LivingOrderDialog open={orderOpen} onOpenChange={setOrderOpen} returnPath="/postinity" onPaid={() => navigate("/postinity/preparer")} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode="login" onSuccess={() => { setAuthOpen(false); setOrderOpen(true); }} />
    </div>
  );
}
